import { S3_BUCKET_URL } from '@/constants';
import { useAccount } from '@/hooks/useAccount';
import type { PerfectLookOptionIdType } from '@/pages/Onboarding/options/PerfectLook/types';
import { getPerfectLookChoiceIdByTitle } from '@/pages/Onboarding/options/PerfectLook/utils/getPerfectLookChoice';
import { preloadImage } from '@/utils/preloadImage';

interface BotInfoType {
  name: string;
  imageUrl: string;
}

type ScreenType = 'summary' | 'paywall';

const PERFECT_LOOK_TO_BOT_MAP: Record<
  PerfectLookOptionIdType,
  Omit<BotInfoType, 'imageUrl'> & { imagePaths: Record<ScreenType, string> }
> = {
  elegant_stylish: {
    name: 'Morgan',
    imagePaths: {
      summary: `${S3_BUCKET_URL}/layout-own-ai-trans/summary/elegant_morgan-baiad1.png`,
      paywall: `${S3_BUCKET_URL}/paywall/trans/elegant_morgan-baiad1.png`,
    },
  },
  athletic_colorful_hair: {
    name: 'Skye',
    imagePaths: {
      summary: `${S3_BUCKET_URL}/layout-own-ai-trans/summary/athletic-with-color-hair_skye-rivers.png`,
      paywall: `${S3_BUCKET_URL}/paywall/trans/athletic-with-color-hair_skye-rivers.png`,
    },
  },
  glamorous_coquette: {
    name: 'Viera',
    imagePaths: {
      summary: `${S3_BUCKET_URL}/layout-own-ai-trans/summary/glamorous_viera-lux.png`,
      paywall: `${S3_BUCKET_URL}/paywall/trans/glamorous_viera-lux.png`,
    },
  },
  natural_beauty_no_makeup: {
    name: 'Ember',
    imagePaths: {
      summary: `${S3_BUCKET_URL}/layout-own-ai-trans/summary/no-makeup_ember-vale.png`,
      paywall: `${S3_BUCKET_URL}/paywall/trans/no-makeup_ember-vale.png`,
    },
  },
};

export const useTransBotInfo = (screen: ScreenType): BotInfoType => {
  const { user } = useAccount();
  const onb_data = user?.onboarding_data || {};
  const perfectLook = getPerfectLookChoiceIdByTitle(onb_data.perfectLook, true);

  if (!perfectLook) {
    const defaultBot = PERFECT_LOOK_TO_BOT_MAP.elegant_stylish;
    return {
      name: defaultBot.name,
      imageUrl: defaultBot.imagePaths[screen],
    };
  }

  const bot =
    PERFECT_LOOK_TO_BOT_MAP[perfectLook] ||
    PERFECT_LOOK_TO_BOT_MAP.elegant_stylish;

  return {
    name: bot.name,
    imageUrl: bot.imagePaths[screen],
  };
};

export const useBotInfoWithPreload = (screen: ScreenType): void => {
  const botInfo = useTransBotInfo(screen);

  if (botInfo?.imageUrl) {
    preloadImage(botInfo.imageUrl);
  }
};
